import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AppointmentChatMessage,
  GetAppointmentOutput,
} from 'graphql/generated/hasura';
import { useGetAppointmentMessageListByAppointmentCodexIdQuery } from 'graphql/generated/remote-schema-hasura';
import { useEffect, useMemo, useState } from 'react';
import { ChatMessage } from '../interfaces';
import { useGetAppointmentDetails } from '../../helpers/useGetAppointmentDetails';
import useParseChatMessages from 'hooks/useParseChatMessages';
import { SharedMediaModalLocationState } from '../../../my-patients/interfaces/interfaces';
import { fromFhirAppointmentTypeToLocal } from 'utilities/functions';
import { PARAM_MODALS_IDENTIFIERS } from 'utilities/constants';
import { useGetAndStoreMediaSignedUrl } from 'hooks/useGetAndStoreMediaSignedUrl';
import {
  AppointmentHasuraStatus,
  MIN_RESCHEDULE_TIME,
} from '../../const/appointment';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';
import { isSummaryForm } from './index';
import moment from 'moment';

export const useGetInfoAppointment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const appointmentIdByState = location.state?.appointmentId as string;
  const [parseMessages, chatMediaLoading] = useParseChatMessages();

  const { appointmentId: appointmentIdByParam } = useParams();
  const appointmentId = appointmentIdByParam || appointmentIdByState;

  const {
    mediaMap,
    appointmentLoading,
    appointmentData,
    patientLoading,
    providerDataLoading,
    providerData,
    patientData,
  } = useGetAppointmentDetails(appointmentId);

  const getMedia = useGetAndStoreMediaSignedUrl();

  const appointment = appointmentData?.getFHIRAppointmentByCodexId.appointment;
  const patient = {
    ...(patientData?.getFHIRPatientbyCodexIDParameter?.users?.[0] || {}),
    id: appointment?.codexPatientId,
  };
  const provider = providerData?.getFHIRProviderbyId?.providers?.[0];

  const { data: messagesData, loading: messageLoading } =
    useGetAppointmentMessageListByAppointmentCodexIdQuery({
      variables: {
        appointmentCodexId: appointmentId,
      },
      onError: (error) => {
        console.warn('An error happened', error);
      },
    });

  const chatMessages: ChatMessage[] = useMemo(
    () =>
      messagesData?.getAppointmentMessageListByAppointmentCodexId.chat &&
      messagesData?.getAppointmentMessageListByAppointmentCodexId.chat.messages
        ? parseMessages(
            messagesData.getAppointmentMessageListByAppointmentCodexId.chat
              .messages as AppointmentChatMessage[],
          ) ?? []
        : [],
    [messagesData, parseMessages],
  );

  const patientFullName = `${patient?.SENSITIVE_firstname || ''} ${
    patient?.SENSITIVE_lastname || ''
  }`;

  const onMediaLocationClick = async (location: string) => {
    if (!appointment) return;
    const currentLocationMediaId = mediaMap.current.get(location);
    if (currentLocationMediaId) {
      const media = appointment.media?.find(
        ({ fileId }) => fileId === currentLocationMediaId,
      );
      const mediaUrl = await getMedia(
        currentLocationMediaId,
        appointment.codexPatientId,
      );
      if (!mediaUrl) return;
      const modalState: SharedMediaModalLocationState = {
        mediaDate: new Date(appointment.start || 0),
        mediaSrc: mediaUrl,
        providerName: patientFullName,
        appointmentType: fromFhirAppointmentTypeToLocal(
          appointment.appointmentType || undefined,
        ),
        bodySite: location,
        description: media?.description || undefined,
      };
      navigate(`.?${PARAM_MODALS_IDENTIFIERS.SHARED_MEDIA_MODAL_ID}=true`, {
        state: modalState,
      });
    }
  };

  return {
    appointment,
    appointmentId,
    provider,
    patient,
    mediaMap,

    appointmentLoading,
    patientLoading,
    providerDataLoading,

    chatMediaLoading,
    chatMessages,
    messageLoading,
    patientFullName,
    onMediaLocationClick,
  };
};

type GetAppointmentStatuses = Omit<GetAppointmentOutput, 'recommendations'>;

export const useGetAppointmentStatuses = (
  appointment?: GetAppointmentStatuses | null,
) => {
  const queryParams = new URLSearchParams(location.search);
  const isForm = queryParams.get(isSummaryForm);

  const [, setTick] = useState(0);

  useEffect(() => {
    if (!appointment?.start) return;

    const delay = moment(appointment.start)
      .subtract(MIN_RESCHEDULE_TIME, 'hours')
      .diff(moment());

    if (delay > 0) {
      const timer = setTimeout(() => setTick((t) => t + 1), delay);
      return () => clearTimeout(timer);
    }
  }, [appointment?.start]);

  const { user } = useAuth();
  const isProvider = user?.role === Roles.PROVIDER;
  const isSubmittedProvider = user?.uuid === appointment?.codexProviderId;

  const isCanRescheduled =
    isSubmittedProvider &&
    moment(appointment?.start).diff(moment(), 'hours', true) >
      MIN_RESCHEDULE_TIME &&
    appointment?.appointmentStatus === AppointmentHasuraStatus.Booked;

  const isProviderMissed =
    appointment?.appointmentStatus ===
    AppointmentHasuraStatus.ProviderNotShowingUp;
  const isPatientMissed =
    appointment?.appointmentStatus ===
    AppointmentHasuraStatus.PatientNotShowingUp;
  const missed =
    appointment?.appointmentStatus ===
    AppointmentHasuraStatus.NobodyNotShowingUp;

  const isCompleted =
    appointment?.appointmentStatus === AppointmentHasuraStatus.Completed ||
    !!isForm;

  const hasntSummary = isCompleted && !appointment?.isSummaryCompleted;

  const hasSummary = isCompleted && appointment?.isSummaryCompleted;

  return {
    isProvider,
    isProviderMissed,
    isPatientMissed,
    isCanRescheduled,
    missed,
    isCompleted,
    hasSummary,
    isSubmittedProvider,
    hasntSummary,
  };
};
